import { Component, type ReactNode } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches React render-time exceptions so a
 * bad page becomes a visible error message instead of a blank screen,
 * AND ships the error to Sentry so it shows up in the dashboard.
 *
 * Mount this just inside <BrowserRouter> so route changes don't reset
 * a captured error — the user can navigate away or click "Reload" to
 * recover.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // Hand the error + React-side component stack to Sentry. The default
    // Sentry SDK browser integration would catch the same error via the
    // window.onerror hook, but the componentStack only flows through this
    // boundary path, so we send it explicitly.
    Sentry.captureException(error, { contexts: { react: info } });
    // Also log to console so a dev open in DevTools sees it immediately.
    console.error("ErrorBoundary captured:", error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Something went wrong rendering this page.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The error has been reported to Sentry. You can try reloading, or go back to the dashboard.
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: "#fafafa",
            fontFamily: "monospace",
            fontSize: "0.85rem",
            overflow: "auto",
            maxHeight: 240,
            whiteSpace: "pre-wrap",
            mb: 2,
            borderRadius: 1,
          }}
        >
          {this.state.error.message}
          {"\n\n"}
          {this.state.error.stack}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={() => window.location.assign("/")}>
            Go to dashboard
          </Button>
          <Button onClick={this.reset}>Reset</Button>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </Box>
      </Box>
    );
  }
}
